import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { PreferredSourceButton } from "@/components/site/PreferredSourceButton";
import { SITE } from "@/lib/site";
import { listAuthorsPublic } from "@/lib/authors.functions";
import { toAuthor, type AuthorRow, type Author } from "@/lib/authors";

export const Route = createFileRoute("/about")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["authors", "public"],
      queryFn: () => listAuthorsPublic(),
    });
  },
  head: () => ({
    meta: [
      { title: `About — ${SITE.name}` },
      {
        name: "description",
        content:
          "Map Observer is the authoritative newsroom on Google Business Profile, the Map Pack, and local search — read by SEO agencies, GBP specialists, and local business owners.",
      },
      { property: "og:title", content: `About — ${SITE.name}` },
      {
        property: "og:description",
        content:
          "How Map Observer reports on Google Business Profile, the Map Pack, and AI search for local SEO agencies and the operators they serve.",
      },
      { property: "og:url", content: `${SITE.url}/about` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/about` }],
  }),
  component: AboutPage,
});

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function AuthorSection({ author }: { author: Author }) {
  return (
    <section
      id={author.slug}
      aria-labelledby={`${author.slug}-heading`}
      className="mt-10 scroll-mt-24 border-t border-border pt-10 first:border-t-0 first:pt-0 first:mt-0"
    >
      <p className="eyebrow">{author.isDefault ? "Editor" : "Contributor"}</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-6">
        <div
          aria-hidden
          className="shrink-0 h-24 w-24 rounded-full bg-paper-soft border border-border grid place-items-center font-serif text-2xl text-ochre-deep overflow-hidden"
        >
          {author.avatar ? (
            <img src={author.avatar} alt="" className="h-full w-full object-cover" />
          ) : (
            initials(author.name)
          )}
        </div>
        <div className="flex-1">
          <h2 id={`${author.slug}-heading`} className="headline-display text-2xl">
            {author.name}
          </h2>
          <p className="mt-3 text-base text-foreground/85 leading-relaxed whitespace-pre-line">
            {author.bio}
          </p>
          <p className="mt-4 text-sm flex flex-wrap gap-x-4 gap-y-1">
            {author.linkedin && (
              <a
                href={author.linkedin}
                target="_blank"
                rel="noopener noreferrer me"
                className="text-ochre-deep hover:text-accent underline"
              >
                LinkedIn ↗
              </a>
            )}
            {author.twitter && (
              <a
                href={author.twitter}
                target="_blank"
                rel="noopener noreferrer me"
                className="text-ochre-deep hover:text-accent underline"
              >
                Twitter / X ↗
              </a>
            )}
            {author.website && (
              <a
                href={author.website}
                target="_blank"
                rel="noopener noreferrer me"
                className="text-ochre-deep hover:text-accent underline"
              >
                Website ↗
              </a>
            )}
          </p>
        </div>
      </div>
    </section>
  );
}

function AuthorsList() {
  const { data } = useSuspenseQuery({
    queryKey: ["authors", "public"],
    queryFn: () => listAuthorsPublic(),
  });
  const rows = (data ?? []) as AuthorRow[];
  if (rows.length === 0) return null;
  return (
    <div className="mt-16 space-y-10">
      {rows.map((r) => (
        <AuthorSection key={r.id} author={toAuthor(r)} />
      ))}
    </div>
  );
}

function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <p className="eyebrow">About</p>
        <h1 className="headline-display mt-3 text-5xl">
          The newsroom for people who care where they appear on the map.
        </h1>
        <div className="prose-article mt-10">
          <p>
            Map Observer is the authoritative newsroom on Google Maps, Google Business Profile,
            the Map Pack, and the AI search systems reshaping local results. We aggregate,
            verify, and explain — pulling signal from Google's official channels, the SEO
            community, and our own monitoring into the definitive daily feed for the industry.
          </p>

          <h2>Who we serve</h2>
          <ul>
            <li>
              <strong>Local SEO agencies and GBP specialists</strong> — the people billing clients
              to defend Map Pack rankings, recover suspensions, and adapt to AI Overviews.
            </li>
            <li>
              <strong>Multi-location and franchise operators</strong> — in-house teams managing
              dozens or thousands of Business Profiles.
            </li>
            <li>
              <strong>Local business owners</strong> — restaurants, clinics, contractors, home
              services, retail — anyone whose phone rings because they show up on the map.
            </li>
          </ul>

          <h2>What you'll get</h2>
          <ul>
            <li>Daily coverage of Google Business Profile product, policy, and spam changes.</li>
            <li>Ranking signal tests, case studies, and the death of folklore-grade advice.</li>
            <li>Reviews policy, suspensions, and reinstatement playbooks.</li>
            <li>Plain-English explainers on AI Overviews, AI Mode, and Ask Maps for local.</li>
            <li>A Friday brief that condenses the week into a 4-minute read.</li>
          </ul>

          <h2>How we work</h2>
          <p>
            Every story is reported, edited, and fact-checked. When we summarize work done
            elsewhere we link to the source. We don't run sponsored articles, paid placements, or
            affiliate content disguised as journalism. We use AI in our pipeline to draft and
            structure — every published piece is reviewed against the original sources before it
            goes live.
          </p>

          <h2>Who funds us</h2>
          <p>
            Map Observer was founded and is funded by{" "}
            <a href="https://gbply.net" rel="sponsored noopener">GBPly</a> — A Smarter Way to
            Manage Google Business Profiles. They pay for this newsroom; they don't decide what it
            publishes. No story is selected, reviewed, or killed by our funder, and any article
            mentioning GBPly or a competitor says so inline. The full detail is on our{" "}
            <Link to="/ownership-and-funding">ownership and funding</Link> page and our{" "}
            <Link to="/sponsors/gbply">sponsor page</Link>.
          </p>


          <h2>Get in touch</h2>
          <p>
            Tips, corrections, and reader letters are welcome. Subscribe to the Friday brief below
            and reply to any issue — every reply hits a human inbox.
          </p>
        </div>

        <Suspense fallback={null}>
          <AuthorsList />
        </Suspense>

        <PreferredSourceButton placement="about" className="mt-12" />

        <NewsletterCTA />
      </main>
      <SiteFooter />
    </div>
  );
}
